import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../utils/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { CurrencyInput } from "../components/ui/currency-input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Plus, Pencil, Trash2, CreditCard, FileText, Banknote, Building2, Printer, Upload, Download, Calendar, File } from "lucide-react";
import AccountMovements from "../components/AccountMovements";
import TransactionHistoryModal from "../components/TransactionHistoryModal";



const CreditCards = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [movementsOpen, setMovementsOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  
  // Statement dialog states
  const [statementsDialogOpen, setStatementsDialogOpen] = useState(false);
  const [statementsCard, setStatementsCard] = useState(null);
  const [statements, setStatements] = useState([]);
  const [statementsLoading, setStatementsLoading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadData, setUploadData] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    notes: "",
    file: null,
  });
  const [uploading, setUploading] = useState(false);
  
  // Payment dialog states
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentCard, setPaymentCard] = useState(null);
  const [cashAccounts, setCashAccounts] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [paymentData, setPaymentData] = useState({
    amount: "",
    payment_source: "",
    source_account_id: "",
    date: new Date().toISOString().split("T")[0],
    description: "",
  });
  
  // Transactions dialog states
  const [transactionsDialogOpen, setTransactionsDialogOpen] = useState(false);
  const [transactionsCard, setTransactionsCard] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    bank_name: "",
    card_number_last4: "",
    credit_limit: "",
    closing_day: 1,
    due_day: 15,
    notes: "",
  });

  useEffect(() => {
    fetchCards();
    fetchAccounts();
  }, []);

  const fetchCards = async () => {
    try {
      const response = await axios.get(`${API}/credit-cards`);
      setCards(response.data);
    } catch (error) {
      console.error("Failed to fetch cards:", error);
      toast.error("Kredi kartları yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const [cashRes, bankRes] = await Promise.all([
        axios.get(`${API}/cash-accounts`),
        axios.get(`${API}/bank-accounts`),
      ]);
      setCashAccounts(cashRes.data);
      setBankAccounts(bankRes.data);
    } catch (error) {
      console.error("Failed to fetch accounts:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.bank_name) {
      toast.error("Kart adı ve banka adı zorunludur");
      return;
    }

    try {
      // Ensure credit_limit and current_debt are numbers, not empty strings
      const dataToSend = {
        ...formData,
        credit_limit: formData.credit_limit === "" || formData.credit_limit === null || formData.credit_limit === undefined 
          ? 0 
          : parseFloat(formData.credit_limit),
        current_debt: formData.current_debt === "" || formData.current_debt === null || formData.current_debt === undefined 
          ? 0 
          : parseFloat(formData.current_debt)
      };

      if (editingCard) {
        await axios.put(`${API}/credit-cards/${editingCard.id}`, dataToSend);
        toast.success("Kredi kartı güncellendi");
      } else {
        await axios.post(`${API}/credit-cards`, dataToSend);
        toast.success("Kredi kartı eklendi");
      }
      setDialogOpen(false);
      resetForm();
      fetchCards();
    } catch (error) {
      console.error("Failed to save card:", error);
      toast.error("İşlem başarısız");
    }
  };

  const handleEdit = (card) => {
    setEditingCard(card);
    setFormData({
      name: card.name,
      bank_name: card.bank_name,
      card_number_last4: card.card_number_last4 || "",
      credit_limit: card.credit_limit,
      closing_day: card.closing_day,
      due_day: card.due_day,
      notes: card.notes || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu kredi kartını silmek istediğinizden emin misiniz?")) return;

    try {
      await axios.delete(`${API}/credit-cards/${id}`);
      toast.success("Kredi kartı silindi");
      fetchCards();
    } catch (error) {
      console.error("Failed to delete card:", error);
      toast.error("Silme işlemi başarısız");
    }
  };

  const resetForm = () => {
    setEditingCard(null);
    setFormData({
      name: "",
      bank_name: "",
      card_number_last4: "",
      credit_limit: "",
      closing_day: 1,
      due_day: 15,
      notes: "",
    });
  };

  const openPaymentDialog = (card) => {
    setPaymentCard(card);
    setPaymentData({
      amount: "",
      payment_source: "",
      source_account_id: "",
      date: new Date().toISOString().split("T")[0],
      description: "",
    });
    setPaymentDialogOpen(true);
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    if (!paymentData.amount || !paymentData.payment_source || !paymentData.source_account_id) {
      toast.error("Tutar, ödeme kaynağı ve hesap seçimi zorunludur");
      return;
    }

    try {
      await axios.post(`${API}/credit-card-payments`, {
        credit_card_id: paymentCard.id,
        amount: parseFloat(paymentData.amount),
        payment_source: paymentData.payment_source,
        source_account_id: paymentData.source_account_id,
        date: paymentData.date,
        description: paymentData.description || `${paymentCard.name} kredi kartı ödemesi`,
      });
      toast.success("Kredi kartı ödemesi yapıldı");
      setPaymentDialogOpen(false);
      fetchCards();
      fetchAccounts();
    } catch (error) {
      console.error("Failed to make payment:", error);
      toast.error(error.response?.data?.detail || "Ödeme işlemi başarısız");
    }
  };

  const getAvailableAccounts = () => {
    if (paymentData.payment_source === "cash") {
      return cashAccounts;
    } else if (paymentData.payment_source === "bank") {
      return bankAccounts;
    }
    return [];
  };

  const handlePrint = () => {
    window.print();
  };

  // Transactions functions
  const openTransactionsDialog = (card) => {
    setTransactionsCard(card);
    setTransactionsDialogOpen(true);
  };

  // Statement functions
  const openStatementsDialog = async (card) => {
    setStatementsCard(card);
    setStatementsDialogOpen(true);
    setStatementsLoading(true);
    try {
      const response = await axios.get(`${API}/credit-cards/${card.id}/statements`);
      setStatements(response.data);
    } catch (error) {
      console.error("Failed to fetch statements:", error);
      toast.error("Ekstreler yüklenemedi");
    } finally {
      setStatementsLoading(false);
    }
  };

  const handleStatementUpload = async (e) => {
    e.preventDefault();
    if (!uploadData.file) {
      toast.error("Lütfen bir PDF dosyası seçin");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadData.file);
      formData.append("month", uploadData.month);
      formData.append("year", uploadData.year);
      if (uploadData.notes) {
        formData.append("notes", uploadData.notes);
      }

      await axios.post(
        `${API}/credit-cards/${statementsCard.id}/statements?month=${uploadData.month}&year=${uploadData.year}${uploadData.notes ? `&notes=${encodeURIComponent(uploadData.notes)}` : ''}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      toast.success("Ekstre yüklendi");
      setUploadDialogOpen(false);
      setUploadData({
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        notes: "",
        file: null,
      });
      // Refresh statements
      const response = await axios.get(`${API}/credit-cards/${statementsCard.id}/statements`);
      setStatements(response.data);
    } catch (error) {
      console.error("Failed to upload statement:", error);
      toast.error(error.response?.data?.detail || "Ekstre yüklenemedi");
    } finally {
      setUploading(false);
    }
  };

  const handleStatementDownload = async (statement) => {
    try {
      const response = await axios.get(
        `${API}/credit-card-statements/${statement.id}/download`,
        { 
          responseType: "blob",
          validateStatus: function (status) {
            return status < 500; // Accept all statuses less than 500
          }
        }
      );
      
      // Check if response is not successful
      if (response.status !== 200) {
        // Try to read error message from blob
        if (response.data instanceof Blob) {
          try {
            const text = await response.data.text();
            const error = JSON.parse(text);
            toast.error(error.detail || "Dosya sunucuda bulunamadı");
          } catch {
            toast.error("Dosya sunucuda bulunamadı. Ekstre silinmiş olabilir.");
          }
        } else {
          toast.error("Dosya indirilemedi");
        }
        return;
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", statement.original_filename || `ekstre_${statement.year}_${statement.month}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download statement:", error);
      toast.error("Dosya indirme sırasında bir hata oluştu");
    }
  };

  const handleStatementDelete = async (statement) => {
    if (!window.confirm(`${statement.year}/${statement.month.toString().padStart(2, '0')} dönemine ait ekstreyi silmek istediğinizden emin misiniz?`)) return;

    try {
      await axios.delete(`${API}/credit-card-statements/${statement.id}`);
      toast.success("Ekstre silindi");
      const response = await axios.get(`${API}/credit-cards/${statementsCard.id}/statements`);
      setStatements(response.data);
    } catch (error) {
      console.error("Failed to delete statement:", error);
      toast.error("Ekstre silinemedi");
    }
  };

  const getMonthName = (month) => {
    const months = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
    return months[month - 1];
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const totalDebt = cards.reduce((sum, card) => sum + card.current_debt, 0);
  const totalLimit = cards.reduce((sum, card) => sum + card.credit_limit, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 print:space-y-4" data-testid="credit-cards-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Kredi Kartları
          </h1>
          <p className="text-slate-500 mt-1">Kredi kartlarınızı yönetin</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handlePrint} className="no-print">
            <Printer className="h-4 w-4 mr-2" /> Yazdır
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-credit-card-btn">
                <Plus className="h-5 w-5 mr-2" />
                Yeni Kredi Kartı
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingCard ? "Kredi Kartı Düzenle" : "Yeni Kredi Kartı"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Kart Adı *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Garanti Bonus"
                    data-testid="credit-card-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Banka Adı *</Label>
                  <Input
                    value={formData.bank_name}
                    onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                    placeholder="Garanti Bankası"
                    data-testid="credit-card-bank-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Son 4 Hane</Label>
                  <Input
                    value={formData.card_number_last4}
                    onChange={(e) => setFormData({ ...formData, card_number_last4: e.target.value })}
                    placeholder="1234"
                    maxLength={4}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Kredi Limiti</Label>
                  <CurrencyInput
                    value={formData.credit_limit}
                    onChange={(val) => setFormData({ ...formData, credit_limit: val })}
                    placeholder="Kredi limiti"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Hesap Kesim Günü</Label>
                  <Input
                    type="number"
                    value={formData.closing_day}
                    onChange={(e) => setFormData({ ...formData, closing_day: parseInt(e.target.value) || 1 })}
                    min="1"
                    max="31"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Son Ödeme Günü</Label>
                  <Input
                    type="number"
                    value={formData.due_day}
                    onChange={(e) => setFormData({ ...formData, due_day: parseInt(e.target.value) || 15 })}
                    min="1"
                    max="31"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notlar</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Ek notlar"
                  rows={2}
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  İptal
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="credit-card-save-btn">
                  {editingCard ? "Güncelle" : "Kaydet"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="bg-gradient-to-r from-red-500 to-red-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100">Toplam Borç</p>
                <p className="text-4xl font-bold tabular-nums mt-1">
                  ₺{totalDebt.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div className="h-16 w-16 rounded-full bg-white/20 flex items-center justify-center">
                <CreditCard className="h-8 w-8" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100">Toplam Limit</p>
                <p className="text-4xl font-bold tabular-nums mt-1">
                  ₺{totalLimit.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div className="h-16 w-16 rounded-full bg-white/20 flex items-center justify-center">
                <CreditCard className="h-8 w-8" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cards Grid */}
      {cards.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cards.map((card) => {
            const usagePercent = card.credit_limit > 0 ? (card.current_debt / card.credit_limit) * 100 : 0;
            return (
              <Card key={card.id} className="card-hover" data-testid={`credit-card-${card.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-purple-50 flex items-center justify-center">
                        <CreditCard className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{card.name}</CardTitle>
                        <p className="text-sm text-slate-500">{card.bank_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setSelectedCard(card); setMovementsOpen(true); }}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        title="Hesap Hareketleri"
                      >
                        <FileText className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(card)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(card.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-slate-500">Borç</span>
                      <span className="font-semibold text-red-600 tabular-nums">
                        ₺{card.current_debt.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${
                          usagePercent > 80 ? "bg-red-500" : usagePercent > 50 ? "bg-amber-500" : "bg-emerald-500"
                        }`}
                        style={{ width: `${Math.min(usagePercent, 100)}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-xs text-slate-500 mt-1">
                      <span>Limit: ₺{card.credit_limit.toLocaleString("tr-TR")}</span>
                      <span>{usagePercent.toFixed(1)}% kullanım</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-slate-500 pt-2 border-t">
                    <span>Kesim: {card.closing_day}. gün</span>
                    <span>Son Ödeme: {card.due_day}. gün</span>
                  </div>
                  {card.card_number_last4 && (
                    <p className="text-xs text-slate-400">**** **** **** {card.card_number_last4}</p>
                  )}
                  {/* Action Buttons */}
                  <div className="flex gap-2 mt-2">
                    <Button
                      onClick={() => openPaymentDialog(card)}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                      data-testid={`pay-card-${card.id}`}
                    >
                      <Banknote className="h-4 w-4 mr-2" />
                      Ödeme Yap
                    </Button>
                    <Button
                      onClick={() => openStatementsDialog(card)}
                      variant="outline"
                      className="flex-1"
                      data-testid={`statements-card-${card.id}`}
                    >
                      <Calendar className="h-4 w-4 mr-2" />
                      Ekstreler
                    </Button>
                  </div>
                  <Button
                    onClick={() => openTransactionsDialog(card)}
                    variant="outline"
                    className="w-full mt-2"
                    data-testid={`transactions-card-${card.id}`}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Borçlanma & Ödeme Geçmişi
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <CreditCard className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Henüz kredi kartı eklenmemiş</p>
          </CardContent>
        </Card>
      )}

      <AccountMovements
        open={movementsOpen}
        onClose={() => setMovementsOpen(false)}
        accountType="credit_card"
        accountId={selectedCard?.id}
        accountName={selectedCard?.name}
      />

      {/* Credit Card Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="max-w-md mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle>Kredi Kartı Ödemesi</DialogTitle>
          </DialogHeader>
          {paymentCard && (
            <form onSubmit={handlePaymentSubmit} className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <CreditCard className="h-8 w-8 text-purple-600" />
                  <div>
                    <p className="font-semibold">{paymentCard.name}</p>
                    <p className="text-sm text-slate-500">{paymentCard.bank_name}</p>
                  </div>
                </div>
                <div className="mt-2 text-sm">
                  <span className="text-slate-500">Mevcut Borç: </span>
                  <span className="font-semibold text-red-600">
                    ₺{paymentCard.current_debt.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Ödeme Tutarı *</Label>
                <CurrencyInput
                  value={paymentData.amount}
                  onChange={(val) => setPaymentData({ ...paymentData, amount: val })}
                  placeholder="Ödeme tutarını girin"
                  data-testid="payment-amount-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Ödeme Kaynağı *</Label>
                <Select
                  value={paymentData.payment_source}
                  onValueChange={(val) => setPaymentData({ ...paymentData, payment_source: val, source_account_id: "" })}
                >
                  <SelectTrigger data-testid="payment-source-select">
                    <SelectValue placeholder="Kaynak seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">
                      <div className="flex items-center gap-2">
                        <Banknote className="h-4 w-4" />
                        Kasa
                      </div>
                    </SelectItem>
                    <SelectItem value="bank">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        Banka
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {paymentData.payment_source && (
                <div className="space-y-2">
                  <Label>{paymentData.payment_source === "cash" ? "Kasa Hesabı" : "Banka Hesabı"} *</Label>
                  <Select
                    value={paymentData.source_account_id}
                    onValueChange={(val) => setPaymentData({ ...paymentData, source_account_id: val })}
                  >
                    <SelectTrigger data-testid="source-account-select">
                      <SelectValue placeholder="Hesap seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {getAvailableAccounts().map((acc) => (
                        <SelectItem key={acc.id} value={acc.id}>
                          <div className="flex items-center justify-between w-full">
                            <span>{acc.name}</span>
                            <span className="text-sm text-slate-500 ml-2">
                              (₺{acc.balance.toLocaleString("tr-TR", { minimumFractionDigits: 2 })})
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label>Ödeme Tarihi</Label>
                <Input
                  type="date"
                  value={paymentData.date}
                  onChange={(e) => setPaymentData({ ...paymentData, date: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>Açıklama</Label>
                <Textarea
                  value={paymentData.description}
                  onChange={(e) => setPaymentData({ ...paymentData, description: e.target.value })}
                  placeholder="Ödeme açıklaması"
                  rows={2}
                />
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setPaymentDialogOpen(false)} className="w-full sm:w-auto">
                  İptal
                </Button>
                <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto" data-testid="payment-submit-btn">
                  Ödeme Yap
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Statements Dialog */}
      <Dialog open={statementsDialogOpen} onOpenChange={setStatementsDialogOpen}>
        <DialogContent className="max-w-2xl mx-4 sm:mx-auto max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-purple-600" />
              {statementsCard?.name} - Hesap Ekstreleri
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Upload Button */}
            <div className="flex justify-end">
              <Button
                onClick={() => setUploadDialogOpen(true)}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Upload className="h-4 w-4 mr-2" />
                Ekstre Yükle
              </Button>
            </div>

            {/* Statements List */}
            {statementsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              </div>
            ) : statements.length > 0 ? (
              <div className="divide-y divide-slate-100 border rounded-lg">
                {statements.map((stmt) => (
                  <div key={stmt.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-red-50 flex items-center justify-center">
                        <File className="h-5 w-5 text-red-600" />
                      </div>
                      <div>
                        <p className="font-medium">
                          {getMonthName(stmt.month)} {stmt.year}
                        </p>
                        <p className="text-sm text-slate-500">
                          {stmt.original_filename} • {formatFileSize(stmt.file_size)}
                        </p>
                        {stmt.notes && (
                          <p className="text-xs text-slate-400 mt-1">{stmt.notes}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleStatementDownload(stmt)}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        title="İndir"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleStatementDelete(stmt)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        title="Sil"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 border rounded-lg bg-slate-50">
                <File className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Henüz ekstre yüklenmemiş</p>
                <p className="text-sm text-slate-400 mt-1">
                  Banka hesap ekstrelerinizi PDF olarak yükleyebilirsiniz
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Statement Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-md mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle>Ekstre Yükle</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleStatementUpload} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Ay</Label>
                <Select
                  value={uploadData.month.toString()}
                  onValueChange={(val) => setUploadData({ ...uploadData, month: parseInt(val) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
                      <SelectItem key={m} value={m.toString()}>
                        {getMonthName(m)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Yıl</Label>
                <Select
                  value={uploadData.year.toString()}
                  onValueChange={(val) => setUploadData({ ...uploadData, year: parseInt(val) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027].map((y) => (
                      <SelectItem key={y} value={y.toString()}>
                        {y}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>PDF Dosyası *</Label>
              <Input
                type="file"
                accept=".pdf"
                onChange={(e) => setUploadData({ ...uploadData, file: e.target.files?.[0] || null })}
                className="cursor-pointer"
              />
              <p className="text-xs text-slate-500">Sadece PDF dosyaları kabul edilir</p>
            </div>

            <div className="space-y-2">
              <Label>Not (Opsiyonel)</Label>
              <Textarea
                value={uploadData.notes}
                onChange={(e) => setUploadData({ ...uploadData, notes: e.target.value })}
                placeholder="Ek not ekleyin..."
                rows={2}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setUploadDialogOpen(false)}
                disabled={uploading}
              >
                İptal
              </Button>
              <Button 
                type="submit" 
                className="bg-purple-600 hover:bg-purple-700"
                disabled={uploading || !uploadData.file}
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Yükleniyor...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Yükle
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Transactions Dialog - Borçlanma ve Ödemeler */}
      <TransactionHistoryModal
        open={transactionsDialogOpen}
        onClose={() => setTransactionsDialogOpen(false)}
        accountType="credit_card"
        account={transactionsCard}
        onDataRefresh={() => {
          fetchCards();
          fetchAccounts();
        }}
      />
    </div>
  );
};

export default CreditCards;
